'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Button } from './ui/button';
import { ScrollReveal } from './ScrollReveal';

export function ConsultingSection() {
  return (
    <section className="bg-background" id="consulting">
      {/* Consulting Section - True split layout */}
      <div className="relative overflow-hidden">
        {/* Mobile: Stacked layout */}
        <div className="lg:hidden">
          {/* Photo - full width on mobile, no overlay */}
          <div className="relative h-[400px] sm:h-[500px]">
            <Image
              src="/consulting.jpg"
              alt="Andrew McCourt"
              fill
              className="object-cover object-top"
              sizes="100vw"
            />
          </div>

          {/* Content panel */}
          <div className="bg-muted px-6 py-12 sm:py-16">
            <ScrollReveal>
              <span className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase text-muted-foreground mb-4">
                <span className="w-6 h-px bg-accent" />
                Consulting
              </span>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
                Partner with Andrew
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Looking for strategic guidance to help your organization thrive?
                Andrew partners with leaders to navigate challenges, develop teams,
                and create meaningful, lasting growth.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <Button
                asChild
                size="lg"
                className="group h-12 px-6 bg-primary hover:bg-primary/90 font-medium transition-all duration-300"
              >
                <Link href="/consulting" className="flex items-center gap-2">
                  Start a Conversation
                  <svg
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </Button>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="mt-8 pt-6 border-t border-border/60 flex flex-wrap gap-3">
                {['Leadership', 'Strategy', 'Growth', 'Teams'].map((area) => (
                  <span
                    key={area}
                    className="px-3 py-1.5 bg-accent/10 text-accent text-sm font-medium"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>

        {/* Desktop: Side-by-side split */}
        <div className="hidden lg:grid lg:grid-cols-2 min-h-[700px]">
          {/* Left: Photo - NO overlay, Andrew's face fully visible */}
          <div className="relative">
            <Image
              src="/consulting.jpg"
              alt="Andrew McCourt"
              fill
              className="object-cover object-center"
              sizes="50vw"
            />
            {/* Subtle vignette only at edges, not covering face */}
            <div className="absolute inset-0 shadow-[inset_-40px_0_60px_-20px_rgba(0,0,0,0.1)]" />
          </div>

          {/* Right: Content on solid background */}
          <div className="relative bg-muted flex items-center">
            {/* Decorative elements */}
            <div className="absolute top-12 right-12 w-24 h-24 rounded-full border border-accent/10" />
            <div className="absolute bottom-12 right-12 w-8 h-8 bg-accent/5 rotate-45" />
            <div className="absolute top-1/2 left-0 w-px h-32 bg-gradient-to-b from-transparent via-accent/20 to-transparent -translate-y-1/2" />

            {/* Subtle texture */}
            <div className="absolute inset-0 opacity-[0.02]" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }} />

            <div className="relative px-12 xl:px-20 py-16 w-full">
              <ScrollReveal>
                <span className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase text-muted-foreground mb-6">
                  <span className="w-6 h-px bg-accent" />
                  Consulting
                </span>
              </ScrollReveal>

              <ScrollReveal delay={100}>
                <h2 className="text-4xl xl:text-5xl font-bold tracking-tight text-foreground mb-6">
                  Partner with<br />Andrew
                </h2>
              </ScrollReveal>

              <ScrollReveal delay={200}>
                <p className="text-lg xl:text-xl text-muted-foreground leading-relaxed mb-8 max-w-lg">
                  Looking for strategic guidance to help your organization thrive?
                  Andrew partners with leaders to navigate challenges, develop teams,
                  and create meaningful, lasting growth through faith-based leadership.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={300}>
                <Button
                  asChild
                  size="lg"
                  className="group h-14 px-8 bg-primary hover:bg-primary/90 font-medium transition-all duration-300 hover:shadow-lg hover:shadow-primary/20"
                >
                  <Link href="/consulting" className="flex items-center gap-2">
                    Start a Conversation
                    <svg
                      className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </Button>
              </ScrollReveal>

              {/* Focus areas */}
              <ScrollReveal delay={400}>
                <div className="mt-12 pt-8 border-t border-border/60 flex flex-wrap gap-4">
                  {['Leadership', 'Strategy', 'Growth', 'Teams'].map((area) => (
                    <span
                      key={area}
                      className="px-4 py-2 bg-accent/10 text-accent text-sm font-medium"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
